import { useLayoutEffect, useRef, useState } from "react";
import {
    COMPACT_CARD_MARGIN_X_PX,
    ENTRY_CARD_MIN_WIDTH_PX,
    ENTRY_GRID_MAX_COLUMNS,
    ENTRY_SECTION_GAP_PX,
} from "./newEntryTypes";
import { getGridContentWidthPx, isHorseTitleCovered } from "./compactEntryGridMeasurement";

/**
 * Measures the collapsed entry grid and picks a column count that keeps every horse title unclipped.
 * Also equalizes compact tile height so Add/Copy cards match the tallest entry card.
 */

interface UseCompactEntryGridLayoutArgs {
    isLoading: boolean;
    useCompactEntryGrid: boolean;
    /** Entry slots plus the two add-action tiles, used as the column-count ceiling. */
    compactGridTileCount: number;
    /** Joined horse titles — when names change, re-run fit. */
    compactEntryTitleKey: string;
    copyingEntryCount: number;
}

export const useCompactEntryGridLayout = ({
    isLoading,
    useCompactEntryGrid,
    compactGridTileCount,
    compactEntryTitleKey,
    copyingEntryCount,
}: UseCompactEntryGridLayoutArgs) => {
    const entryGridRef = useRef<HTMLDivElement | null>(null);
    const [entryGridColumns, setEntryGridColumns] = useState(1);
    const [compactTileMinHeightPx, setCompactTileMinHeightPx] = useState<number | undefined>(undefined);
    /** Only when 1 full-width card still can't fit a single word (e.g. CheeseCake on a tiny phone). */
    const [allowBreakLongWords, setAllowBreakLongWords] = useState(false);

    // Prefer as many columns as fit; never crush below ENTRY_CARD_MIN_WIDTH_PX. Break long words only at 1-col overflow.
    // Depend on isLoading: entries are set while the skeleton is still up, so the grid ref is null until loading ends.
    useLayoutEffect(() => {
        if (isLoading || !useCompactEntryGrid) {
            setEntryGridColumns(1);
            setCompactTileMinHeightPx(undefined);
            setAllowBreakLongWords(false);
            return;
        }
        const gridEl = entryGridRef.current;
        if (!gridEl) return;

        const applyGridColumns = (cols: number) => {
            gridEl.style.display = "grid";
            gridEl.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
            gridEl.style.columnGap = "0px";
            gridEl.style.rowGap = `${ENTRY_SECTION_GAP_PX}px`;
            // Force layout so title measurements below see the new column widths.
            void gridEl.offsetWidth;
        };

        const fitColumns = () => {
            const gridWidth = getGridContentWidthPx(gridEl);
            const cs = window.getComputedStyle(gridEl);
            const contentWidth = Math.max(0, gridWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0));
            const minColWidth = ENTRY_CARD_MIN_WIDTH_PX + COMPACT_CARD_MARGIN_X_PX * 2;
            const maxByWidth = Math.max(1, Math.floor(contentWidth / minColWidth));
            const maxCols = Math.min(ENTRY_GRID_MAX_COLUMNS, compactGridTileCount, maxByWidth);
            const titles = Array.from(gridEl.querySelectorAll<HTMLElement>("[data-entry-horse-title]"));

            let chosen = maxCols;
            if (titles.length > 0) {
                chosen = 1;
                // Try max columns first, then drop until every horse title's longest word fits.
                for (let cols = maxCols; cols >= 1; cols--) {
                    applyGridColumns(cols);
                    titles.forEach((el) => {
                        void el.offsetWidth;
                        void (el.closest("[data-entry-title-column]") as HTMLElement | null)?.offsetWidth;
                        void (el.closest("[data-compact-entry-slot]") as HTMLElement | null)?.offsetWidth;
                    });

                    const anyCovered = titles.some(isHorseTitleCovered);
                    if (!anyCovered) {
                        chosen = cols;
                        break;
                    }
                    chosen = cols;
                }
            } else {
                applyGridColumns(maxCols);
            }

            setEntryGridColumns((prev) => (prev === chosen ? prev : chosen));

            // Single-word wrap only if even one full-width card can't fit the longest token.
            let shouldBreakLongWords = false;
            if (chosen === 1 && titles.length > 0) {
                applyGridColumns(1);
                titles.forEach((el) => void el.offsetWidth);
                shouldBreakLongWords = titles.some(isHorseTitleCovered);
            }
            setAllowBreakLongWords((prev) => (prev === shouldBreakLongWords ? prev : shouldBreakLongWords));

            // Stretch Add/Copy tiles to the tallest compact entry card so the row looks even.
            const entryCardWrappers = gridEl.querySelectorAll<HTMLElement>("[data-compact-entry-card]");
            let maxCardHeight = 0;
            entryCardWrappers.forEach((wrapper) => {
                const ionCard = wrapper.querySelector("ion-card") as HTMLElement | null;
                maxCardHeight = Math.max(maxCardHeight, ionCard?.offsetHeight ?? wrapper.offsetHeight);
            });
            setCompactTileMinHeightPx((prev) => {
                const next = maxCardHeight > 0 ? maxCardHeight : undefined;
                return prev === next ? prev : next;
            });
        };

        let cancelled = false;
        let raf2 = 0;
        const scheduleFit = () => {
            if (cancelled) return;
            raf2 = requestAnimationFrame(() => {
                if (!cancelled) fitColumns();
            });
        };
        const raf1 = requestAnimationFrame(scheduleFit);
        // Custom fonts can change measured word width after first paint.
        const fontsReady = typeof document !== "undefined" && document.fonts?.ready ? document.fonts.ready.then(scheduleFit) : Promise.resolve();
        const resizeObserver = new ResizeObserver(scheduleFit);
        resizeObserver.observe(gridEl);
        if (typeof window !== "undefined") {
            window.addEventListener("resize", scheduleFit);
        }
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            resizeObserver.disconnect();
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", scheduleFit);
            }
            void fontsReady;
            gridEl.style.display = "";
            gridEl.style.gridTemplateColumns = "";
            gridEl.style.columnGap = "";
            gridEl.style.rowGap = "";
        };
    }, [isLoading, useCompactEntryGrid, compactGridTileCount, compactEntryTitleKey, copyingEntryCount]);

    return {
        entryGridRef,
        entryGridColumns,
        compactTileMinHeightPx,
        allowBreakLongWords,
    };
};
